import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IDocument, EasyDgdService, ShippingPaper } from '@hazmatsuite-next/easydgd-lib';
import { ActionType, AddressType, BaseResultModel, LanguageService, LicenseDependentButtonModel, PaymentType, TransportMode } from '@hazmatsuite-next/ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { map, Observable, take, tap, debounceTime, switchMap } from 'rxjs';
import { EasyDgdDataState } from '../../store/easydgd-data.reducer';
@UntilDestroy()
@Component({
  selector: 'easydgd-general-information',
  templateUrl: './general-information.component.html',
  styleUrls: ['./general-information.component.scss']
})
export class GeneralInformationComponent {

  hideDocumentLoadFunctionality = false;

  public data$ = this.activatedRoute.data.pipe(
    tap(() => {
      this.initializeForm(this.easydgdService?.data?.ShippingPaper);
    }));

  public generalInformationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['GeneralInformationResource']));
  public validationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));
  public addressListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['AddressListResource']));
  public shippingPaperListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ShippingPaperListResource']));

  public transportMode = TransportMode.Unknown;

  public formGroup: FormGroup;
  public documentsModalOpen = false;
  public transportModes = TransportMode;
  public addressTypes = AddressType;

  public licenseBtnModel: LicenseDependentButtonModel = {
    IsHeroButton: false,
    ButtonAlignmentRight: false,
    ContactText: '',
    OpenInNewTab: false,
    IsPaymentDetailView: false,
    PaymentType: PaymentType.Undefined,
    ActionType: ActionType.Undefined,
    ButtonIcon: 'ifc-custom ifc-folder-open',
    ShowCreditsModal: false,
    Toolname: '',
    FullWidth: false
  }
  public addressLicenseBtnModel: LicenseDependentButtonModel = {
    IsHeroButton: false,
    ButtonAlignmentRight: false,
    ContactText: '',
    OpenInNewTab: false,
    IsPaymentDetailView: false,
    PaymentType: PaymentType.Undefined,
    ActionType: ActionType.Undefined,
    ButtonIcon: 'ifc-custom ifc-book',
    ShowCreditsModal: false,
    Toolname: '',
    FullWidth: false
  }

  private step = 10;

  public allDocuments: IDocument[] = [];
  public currentItemList: IDocument[] = [];

  public startIndex = 0;
  public endIndex = this.step;
  public isLastPage = true;
  public pageIndex = 1;
  public totalPages = 1;
  public currentPageEntries = 0;
  public totalItems = 0;

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private easydgdService: EasyDgdService,
    private languageService: LanguageService,
    private easyDgdStore: Store<{ easyDgdState: EasyDgdDataState }>) {

    this.formGroup = this.builder.group({
      name: new FormControl('', Validators.required),
      description: new FormControl(''),
      shipper: new FormControl('', Validators.required),
      consignee: new FormControl('', Validators.required),
      reference: new FormControl(''),
      additionalhandlinginformation: new FormControl(''),
      portofdeparture: new FormControl(''),
      portofdestination: new FormControl(''),
      airWayBill: new FormControl(''),
      airPortFrom: new FormControl(''),
      airPortTo: new FormControl(''),
      emergencyTelephoneNumber: new FormControl(''),
      originalReceivedElectronically: new FormControl(''),
      signatoryName: new FormControl(''),
      signatoryTitle: new FormControl(''),
      signatoryPlace: new FormControl(''),
      shippingDate: new FormControl('', Validators.required),
      vehicleId: new FormControl(''),
      grossMass: new FormControl(''),
      sealNumbers: new FormControl(''),
      packingCertificateSignatoryName: new FormControl(''),
      signatoryRole: new FormControl(''),
      signatoryCompanyName: new FormControl(''),
      packingPlace: new FormControl(''),
      packingDate: new FormControl(''),
      isMultimodalTransportation: new FormControl(''),
      isShippingDateUnknown: new FormControl(''),
    });

    this.easyDgdStore.select(state => state.easyDgdState.transportMode).pipe(
      untilDestroyed(this)
    ).subscribe(transportMode => {
      this.transportMode = transportMode;
      if (this.transportMode === TransportMode.Air) {
        this.signatoryName?.addValidators(Validators.required);
      }
      else if (this.transportMode === TransportMode.Sea) {
        this.signatoryName?.addValidators(Validators.required);
        this.signatoryPlace?.addValidators(Validators.required);
      } else {
        this.signatoryName?.removeValidators(Validators.required);
        this.signatoryPlace?.removeValidators(Validators.required);
      }
    });

    this.loadDocuments();

    this.formGroup.valueChanges.pipe(
      untilDestroyed(this),
      debounceTime(200),
      switchMap(() => this.saveModel())
    ).subscribe();
  }

  saveModel(): Observable<boolean> {
    this.easydgdService.setGeneralInformationData({
      Id: this.easydgdService.data?.ShippingPaper?.Id,
      Consignee: this.consignee?.value,
      HideDocumentLoadFunctionality: this.hideDocumentLoadFunctionality,
      Name: this.name?.value,
      Shipper: this.shipper?.value,
      ShippingDate: this.shippingDate?.value,
      AdditionalHandlingInformation: this.additionalhandlinginformation?.value,
      AirWayBill: this.airWayBill?.value,
      Description: this.description?.value,
      EmergencyTelephoneNumber: this.emergencyTelephoneNumber?.value,
      GrossMass: this.grossMass?.value,
      //IsMultimodalTransportation: this.isMultimodalTransportation?.value,
      OriginalReceivedElectronically: this.originalReceivedElectronically?.value,
      PackingCertificateSignatoryName: this.packingCertificateSignatoryName?.value,
      PackingDate: this.packingDate?.value,
      PackingPlace: this.packingPlace?.value,
      PortOfDeparture: this.portofdeparture?.value,
      PortOfDestination: this.portofdestination?.value,
      Reference: this.reference?.value,
      SealNumbers: this.sealNumbers?.value,
      SignatoryCompanyName: this.signatoryCompanyName?.value,
      SignatoryName: this.signatoryName?.value,
      SignatoryPlace: this.signatoryPlace?.value,
      SignatoryRole: this.signatoryRole?.value,
      SignatoryTitle: this.signatoryTitle?.value,
      ContainerVehicleIdentification: this.vehicleId?.value,
      CompositePackagingItems: [],
      InnerPackagingItems: [],
      OverpackPackagingItems: [],
      SinglePackagingItems: []
    });

    return this.easydgdService.saveData();
  }



  openDocumentsModal(): void {
    this.documentsModalOpen = true;
  }

  identify(_: number, item: IDocument) {
    return item.Id;
  }

  enableStepHistory(): void {
    //this.easyDgdService.enableStepHistory().pipe(take(1)).subscribe();
  }

  public editShippingPaper(id: number): void {
    this.easydgdService.editShippingPaper(id).pipe(
      take(1)
    ).subscribe(() => {
      this.initializeForm(this.easydgdService?.data?.ShippingPaper);
      this.closeDocumentsModal();
    });
  }

  public cloneShippingPaper(id: number): void {
    this.easydgdService.cloneShippingPaper(id).pipe(
      take(1)
    ).subscribe(() => {
      this.initializeForm(this.easydgdService?.data?.ShippingPaper)
      this.closeDocumentsModal();
    });
  }

  public deleteShippingPaper(id: number): void {
    this.easydgdService.deleteShippingPaper(id).pipe(
      take(1)
    ).subscribe((result: BaseResultModel) => {
      if (result?.Success) {
        this.allDocuments = this.allDocuments.filter(doc => doc.Id !== id);
        this.selectCurrentItems();
        this.closeDocumentsModal();
      }
    });
  }

  public navigateToAddressList(type: AddressType): void {
    this.router.navigate([`../addresslist/${type}`], { relativeTo: this.activatedRoute })
  }

  public changeShippingDateUnknown(evt: Event) {
    if ((evt.currentTarget as HTMLInputElement).checked) {
      this.easydgdService.setGeneralInformationData({
        ...this.easydgdService.getGeneralInformationData(),
        IsShippingDateUnknown: true
      })
      this.shippingDate?.setValue(new Date());
      this.shippingDate?.disable();
    } else {
      this.easydgdService.setGeneralInformationData({
        ...this.easydgdService.getGeneralInformationData(),
        IsShippingDateUnknown: false
      })
      this.shippingDate?.enable();
    }
  }

  public goToFirstPage(): void {
    if (this.startIndex !== 0) {
      this.startIndex = 0;
      this.endIndex = this.step;
    }
    this.selectCurrentItems();
  }

  public goToLastPage(): void {
    this.endIndex = this.totalPages * 10;
    this.startIndex = this.endIndex - this.step;
    this.selectCurrentItems();
  }

  public goToPreviousPage(): void {
    this.startIndex -= this.step;
    this.endIndex -= this.step;
    this.selectCurrentItems();
  }

  public goToNextPage(): void {
    this.startIndex += this.step;
    this.endIndex += this.step;
    this.selectCurrentItems();
  }

  private closeDocumentsModal(): void {
    this.documentsModalOpen = false;
  }

  private initializeItems(): void {
    this.totalPages = Math.ceil(this.allDocuments.length / this.step);
    if (this.allDocuments.length < this.endIndex - this.step) {
      this.resetIndex();
    }
    this.selectCurrentItems();
  }

  private selectCurrentItems(): void {
    this.isLastPage = this.endIndex >= this.allDocuments.length;
    this.currentItemList = this.allDocuments.slice(this.startIndex, this.isLastPage ? this.allDocuments.length : this.endIndex);
    this.pageIndex = this.startIndex === 0 ? 1 : (this.startIndex / this.step) + 1;
    this.currentPageEntries = this.currentItemList.length;
    this.totalItems = this.allDocuments.length;
  }

  private resetIndex(): void {
    this.startIndex = 0;
    this.endIndex = this.step;
  }

  private loadDocuments() {
    this.easydgdService.loadDocuments().pipe(
      take(1)
    ).subscribe((documents: IDocument[]) => {
      this.allDocuments = documents;
      this.initializeItems();
    });
  }

  private initializeForm(shippingPaper?: ShippingPaper): void {
    this.formGroup.patchValue({
      name: shippingPaper?.Name,
      description: shippingPaper?.Description,
      shipper: shippingPaper?.Shipper,
      consignee: shippingPaper?.Consignee,
      reference: shippingPaper?.Reference,
      additionalhandlinginformation: shippingPaper?.AdditionalHandlingInformation,
      portofdeparture: shippingPaper?.PortOfDeparture,
      portofdestination: shippingPaper?.PortOfDestination,
      airWayBill: shippingPaper?.AirWayBill,
      emergencyTelephoneNumber: shippingPaper?.EmergencyTelephoneNumber,
      originalReceivedElectronically: shippingPaper?.OriginalReceivedElectronically,
      signatoryName: shippingPaper?.SignatoryName,
      signatoryTitle: shippingPaper?.SignatoryTitle,
      signatoryPlace: shippingPaper?.SignatoryPlace,
      shippingDate: shippingPaper?.ShippingDate,
      vehicleId: shippingPaper?.ContainerVehicleIdentification,
      grossMass: shippingPaper?.GrossMass,
      sealNumbers: shippingPaper?.SealNumbers,
      packingCertificateSignatoryName: shippingPaper?.PackingCertificateSignatoryName,
      signatoryRole: shippingPaper?.SignatoryRole,
      signatoryCompanyName: shippingPaper?.SignatoryCompanyName,
      packingPlace: shippingPaper?.PackingPlace,
      packingDate: shippingPaper?.PackingDate,
      isMultimodalTransportation: shippingPaper?.IsMultimodalTransportation,
    }, { emitEvent: false });

    this.hideDocumentLoadFunctionality = shippingPaper?.HideDocumentLoadFunctionality || false;
  }

  get name(): AbstractControl | null {
    return this.formGroup.get('name');
  }
  get description(): AbstractControl | null {
    return this.formGroup.get('description');
  }
  get shipper(): AbstractControl | null {
    return this.formGroup.get('shipper');
  }
  get consignee(): AbstractControl | null {
    return this.formGroup.get('consignee');
  }
  get reference(): AbstractControl | null {
    return this.formGroup.get('reference');
  }
  get additionalhandlinginformation(): AbstractControl | null {
    return this.formGroup.get('additionalhandlinginformation');
  }
  get portofdeparture(): AbstractControl | null {
    return this.formGroup.get('portofdeparture');
  }
  get portofdestination(): AbstractControl | null {
    return this.formGroup.get('portofdestination');
  }
  get airWayBill(): AbstractControl | null {
    return this.formGroup.get('airWayBill');
  }
  get emergencyTelephoneNumber(): AbstractControl | null {
    return this.formGroup.get('emergencyTelephoneNumber');
  }
  get originalReceivedElectronically(): AbstractControl | null {
    return this.formGroup.get('originalReceivedElectronically');
  }
  get signatoryName(): AbstractControl | null {
    return this.formGroup.get('signatoryName');
  }
  get signatoryTitle(): AbstractControl | null {
    return this.formGroup.get('signatoryTitle');
  }
  get signatoryPlace(): AbstractControl | null {
    return this.formGroup.get('signatoryPlace');
  }
  get shippingDate(): AbstractControl | null {
    return this.formGroup.get('shippingDate');
  }
  get vehicleId(): AbstractControl | null {
    return this.formGroup.get('vehicleId');
  }
  get grossMass(): AbstractControl | null {
    return this.formGroup.get('grossMass');
  }
  get sealNumbers(): AbstractControl | null {
    return this.formGroup.get('sealNumbers');
  }
  get packingCertificateSignatoryName(): AbstractControl | null {
    return this.formGroup.get('packingCertificateSignatoryName');
  }
  get signatoryRole(): AbstractControl | null {
    return this.formGroup.get('signatoryRole');
  }
  get signatoryCompanyName(): AbstractControl | null {
    return this.formGroup.get('signatoryCompanyName');
  }
  get packingPlace(): AbstractControl | null {
    return this.formGroup.get('packingPlace');
  }
  get packingDate(): AbstractControl | null {
    return this.formGroup.get('packingDate');
  }
  get isMultimodalTransportation(): AbstractControl | null {
    return this.formGroup.get('isMultimodalTransportation');
  }
  get isShippingDateUnknown(): AbstractControl | null {
    return this.formGroup.get('isShippingDateUnknown');
  }
}
